/**
 * Skill Center data hooks (SKC-T3.3 / P-COND-B13A4C16E0A2).
 *
 * - `useSkillCapabilities()` — the three capability flags. Every Skill Center
 *   entry (①②②'③) renders ONLY when `skill_center_enabled === true` (R32).
 *   A failed fetch answers all false but is NOT cached (next mount retries);
 *   a successful answer is cached for the page lifetime so the several
 *   entries on one screen share one request.
 * - `useEffectiveSkills(projectId, mode, conversationMode?)` — `GET /projects/{id}/skills/effective`
 *   with a short per-(project, mode, conversation mode) cache (SKC-T7). The
 *   Skill Center handoff may `peekEffectiveSkills` to select instantly, but it
 *   ALWAYS re-verifies with `loadEffectiveSkills(..., { force: true })` and
 *   drops the selection if the fresh list says the skill is unusable.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ConversationSkillMode,
  EffectiveSkillsContext,
  EffectiveSkillsMode,
  EffectiveSkillsResponse,
  SkillCapabilities,
  SKILL_CAPABILITIES_DISABLED,
  fetchEffectiveSkills,
  fetchSkillCapabilities,
} from '../services/skills';

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

let capabilitiesPromise: Promise<SkillCapabilities> | null = null;
let capabilitiesValue: SkillCapabilities | null = null;

/** Shared, cached capability load (never rejects). A failed fetch (network
 *  error / 404 / 5xx — `fetchSkillCapabilities({ strict: true })` rethrows)
 *  answers all-false for THIS call but is not memoized, so the next caller
 *  (e.g. the next mount of `useSkillCapabilities`) retries. */
export function loadSkillCapabilities(): Promise<SkillCapabilities> {
  if (!capabilitiesPromise) {
    capabilitiesPromise = fetchSkillCapabilities({ strict: true }).then(
      (caps) => {
        capabilitiesValue = caps;
        return caps;
      },
      () => {
        capabilitiesPromise = null;
        return { ...SKILL_CAPABILITIES_DISABLED };
      },
    );
  }
  return capabilitiesPromise;
}

/** Test / logout hook: forget the cached capabilities. */
export function resetSkillCapabilitiesCache(): void {
  capabilitiesPromise = null;
  capabilitiesValue = null;
}

export interface SkillCapabilitiesState extends SkillCapabilities {
  /** True until the first load settles (entries stay hidden meanwhile). */
  loading: boolean;
}

export function useSkillCapabilities(): SkillCapabilitiesState {
  const [caps, setCaps] = useState<SkillCapabilities | null>(capabilitiesValue);
  useEffect(() => {
    if (caps) return undefined;
    let cancelled = false;
    loadSkillCapabilities().then((c) => {
      if (!cancelled) setCaps(c);
    });
    return () => {
      cancelled = true;
    };
  }, [caps]);
  return { ...(caps || SKILL_CAPABILITIES_DISABLED), loading: caps === null };
}

// ---------------------------------------------------------------------------
// Effective skills
// ---------------------------------------------------------------------------

/** Client cache TTL (technical knob, not a business value). */
const EFFECTIVE_CACHE_TTL_MS = 30_000;

const effectiveCache = new Map<string, { at: number; data: EffectiveSkillsResponse }>();

/** Cache key = (project, mode, conversation mode) — SKC-T7: `mode=project`
 *  answers depend on the conversation mode for dual-mode projects. */
const cacheKey = (
  projectId: string,
  mode?: string | null,
  conversationMode?: string | null,
): string => `${projectId}|${mode || ''}|${(mode === 'project' && conversationMode) || ''}`;

/** Normalise the conversation mode exactly like the request: it only applies
 *  to `mode='project'` (shared by load + peek so their keys never diverge). */
const effectiveKey = (
  projectId: string,
  mode?: string | null,
  conversationMode?: ConversationSkillMode | null,
): string => cacheKey(projectId, mode, mode === 'project' ? conversationMode || null : null);

/**
 * Synchronous cache read: the list last loaded for this key if still within
 * the TTL, else null. Never fetches. Used by the Skill Center handoff to
 * select instantly from the list the Skill Center just showed; the caller
 * MUST still re-verify with `loadEffectiveSkills(..., { force: true })`.
 */
export function peekEffectiveSkills(
  projectId: string,
  mode?: string | null,
  conversationMode?: ConversationSkillMode | null,
): EffectiveSkillsResponse | null {
  const hit = effectiveCache.get(effectiveKey(projectId, mode, conversationMode));
  return hit && Date.now() - hit.at < EFFECTIVE_CACHE_TTL_MS ? hit.data : null;
}

export async function loadEffectiveSkills(
  projectId: string,
  mode?: string | null,
  opts?: { force?: boolean; conversationMode?: ConversationSkillMode | null },
): Promise<EffectiveSkillsResponse> {
  const conv = mode === 'project' ? opts?.conversationMode || null : null;
  const key = effectiveKey(projectId, mode, conv);
  const hit = effectiveCache.get(key);
  if (!opts?.force && hit && Date.now() - hit.at < EFFECTIVE_CACHE_TTL_MS) {
    return hit.data;
  }
  // Old (two-arg) call shape unchanged for dev / collab / unfiltered lists.
  const data = mode === 'project'
    ? await fetchEffectiveSkills(projectId, mode || undefined, conv)
    : await fetchEffectiveSkills(projectId, mode || undefined);
  effectiveCache.set(key, { at: Date.now(), data });
  return data;
}

/** Drop cached lists (one project, or all) after a write. */
export function invalidateEffectiveSkills(projectId?: string): void {
  if (!projectId) {
    effectiveCache.clear();
    return;
  }
  Array.from(effectiveCache.keys()).forEach((k) => {
    if (k.startsWith(`${projectId}|`)) effectiveCache.delete(k);
  });
}

export interface EffectiveSkillsState {
  data: EffectiveSkillsResponse | null;
  /** SKC-T7: `data.context` shortcut (null until loaded / on old responses). */
  context: EffectiveSkillsContext | null;
  loading: boolean;
  error: boolean;
  /** Force a fresh fetch (bypasses the cache). */
  reload: () => void;
}

/**
 * `mode`: 'dev' | 'collab' | 'project' | null (null = no filter).
 * `conversationMode` is only sent with `mode='project'`.
 * Data is tagged with the key it was loaded for, so a rapid project / mode
 * switch never shows the previous project's list (stale responses are
 * dropped by the effect cleanup AND by the key check below).
 */
export function useEffectiveSkills(
  projectId: string | null | undefined,
  mode?: EffectiveSkillsMode | string | null,
  conversationMode?: ConversationSkillMode | null,
): EffectiveSkillsState {
  const key = projectId ? effectiveKey(projectId, mode, conversationMode) : '';
  const [loaded, setLoaded] = useState<{ key: string; data: EffectiveSkillsResponse } | null>(null);
  const [loading, setLoading] = useState<boolean>(!!projectId);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!projectId) {
      setLoaded(null);
      setLoading(false);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    loadEffectiveSkills(projectId, mode, { force: nonce > 0, conversationMode })
      .then((d) => {
        if (!cancelled) setLoaded({ key, data: d });
      })
      .catch(() => {
        if (!cancelled) {
          setLoaded(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `key` is derived from the three inputs below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, mode, conversationMode, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const data = loaded && loaded.key === key ? loaded.data : null;
  return {
    data,
    context: data?.context ?? null,
    loading: loading || (!!projectId && !data && !error),
    error,
    reload,
  };
}
